import { TitleCasePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiUrls } from 'src/app/core/classes/ApiUrls';
import { Helper } from 'src/app/shared/classes/Helper';
import { HitApi } from 'src/app/shared/classes/HitApi';
import { Widget } from 'src/app/shared/classes/Widget';
import { WidgetInjectedData } from 'src/app/shared/classes/WidgetInjectedData';
import { AuthorizationType } from 'src/app/shared/enums/AuthorizationType';
import { ButtonColorType } from 'src/app/shared/enums/ButtonColorType';
import { FilterStoreKey } from 'src/app/shared/enums/FilterStoreKey';
import { IconType } from 'src/app/shared/enums/IconType';
import { ModalAction } from 'src/app/shared/enums/ModalAction';
import { ModalType } from 'src/app/shared/enums/ModalType';
import { RequestType } from 'src/app/shared/enums/RequestType';
import { IButtonGeneratorInput } from 'src/app/shared/interfaces/button-generator/IButtonGeneratorInput';
import { ICardsGeneratorInput } from 'src/app/shared/interfaces/card-generator/ICardsGeneratorInput';
import { IHitApi } from 'src/app/shared/interfaces/hit-api/IHitApi';
import { IIcon } from 'src/app/shared/interfaces/icon-data/IIcon';
import { IModalData } from 'src/app/shared/interfaces/modal/IModalData';
import { ModalService } from 'src/app/shared/services/modal/modal-service/modal.service';
import { MultiStepFormService } from 'src/app/shared/services/modal/multi-step-form/multi-step-form.service';
import { ButtonType } from './../../../../enums/ButtonType';
import { CreateMgReportStepOneComponent } from './../../../modal-templates/managed-report-listing-modal/create-mg-report-step-one/create-mg-report-step-one.component';
import { CreateMgReportStepThreeComponent } from './../../../modal-templates/managed-report-listing-modal/create-mg-report-step-three/create-mg-report-step-three.component';
import { CreateMgReportStepTwoComponent } from './../../../modal-templates/managed-report-listing-modal/create-mg-report-step-two/create-mg-report-step-two.component';

@Component({
    selector: 'app-managed-report-listing',
    templateUrl: './managed-report-listing.component.html',
    styleUrls: ['./managed-report-listing.component.sass']
})
export class ManagedReportListingComponent implements OnInit {
    widgetRef: Widget;
    cardInput: ICardsGeneratorInput;

    constructor(
        widgetData: WidgetInjectedData,
        private multiStepFormService: MultiStepFormService,
        private modalService: ModalService
    ) {
        this.widgetRef = widgetData.widgetRef;
    }

    ngOnInit(): void {
        this.cardInput = {
            widgetIconData: {
                type: IconType.FONTAWSOME,
                class: 'fas fa-list-ol'
            },
            cardTitle: {
                infoTitle: 'Report Name',
                infoKey: 'reportName'
            },
            noDataMessage: 'No reports available',
            infoData: [
                {
                    infoTitle: 'Created By',
                    infoKey: 'createdBy|email'
                },
                {
                    infoTitle: 'Frequency',
                    infoKey: 'cronExpression',
                    infoRenderer: (infoData) => {
                        return new TitleCasePipe().transform(
                            infoData['reportFilters']['reportTimespan']
                        );
                    }
                }
            ],
            buttons: [
                {
                    buttonName: 'New Report',
                    buttonType: ButtonType.ICON,
                    buttonColorType: ButtonColorType.PRIMARY,
                    hoverText: 'New Report',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-plus'
                    },
                    function: (buttonData: IButtonGeneratorInput) => {
                        this.create();
                    }
                }
            ],
            cardButtons: [
                {
                    buttonName: null,
                    buttonType: ButtonType.ICON,
                    buttonColorType: ButtonColorType.SECONDARY,
                    hoverText: 'Edit Report',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-edit'
                    },
                    function: (buttonData: IButtonGeneratorInput, cardData) => {
                        // Edit Report
                        this.update(cardData);
                    }
                },
                {
                    buttonName: null,
                    buttonType: ButtonType.ICON,
                    buttonColorType: ButtonColorType.WARN,
                    hoverText: 'Delete Report',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-trash'
                    },
                    function: (buttonData: IButtonGeneratorInput, cardData) => {
                        // Delete Report
                        this.delete(cardData);
                    }
                }
            ],
            titleActions: [
                {
                    buttonName: '',
                    showLoader: true,
                    function: (buttonRef: IButtonGeneratorInput, cardData) => {
                        this.toggleScheduler(cardData);
                    },
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-power-off'
                    },
                    ngStyleRenderer: (cardData) => {
                        return { color: cardData.active ? 'green' : 'red' };
                    },
                    hoverTextRenderer: (cardData) => {
                        return cardData.active
                            ? 'Disable Report'
                            : 'Enable Report';
                    }
                }
            ],
            listExtraction: {
                type: 'DIRECT'
            },
            bulkActions: [
                {
                    buttonName: 'Delete',
                    function: (
                        buttonData: IButtonGeneratorInput,
                        selectedCards,
                        bulkLoader: BehaviorSubject<boolean>
                    ) => {
                        this.modalService.openConfirmationModal({
                            modalName: 'Delete Reports',
                            modalIcon: {
                                type: IconType.FONTAWSOME,
                                class: 'fas fa-trash'
                            },
                            contextIcon: {
                                type: IconType.FONTAWSOME,
                                class: 'fas fa-exclaimation-triangle'
                            },
                            confirmationMessage:
                                'Are you sure you want to delete selected reports ?',
                            buttonColorType: ButtonColorType.WARN,
                            buttonText: 'Delete',
                            loaderOnExec: true,
                            function: (modalId: Symbol) => {
                                bulkLoader.next(true);
                                // Bulk Delete
                                const batchDeleteInput = {
                                    ids: selectedCards.map(
                                        (selCard) => selCard.id
                                    )
                                };
                                const apiArgs: IHitApi = {
                                    url: `${ApiUrls.REPORT_ENDPOINT}/batch`,
                                    intactUrl: `${ApiUrls.REPORT_ENDPOINT}/{batch}`,
                                    input: batchDeleteInput,
                                    uniqueIdentity: Symbol(),
                                    requestType: RequestType.DELETE,
                                    config: {
                                        authorization:
                                            AuthorizationType.BEARER_TOKEN
                                    },
                                    function: (response) => {
                                        this.widgetRef.notificationsService.showSnackBar(
                                            'Reports deleted successfully'
                                        );
                                        bulkLoader.next(false);
                                        this.widgetRef.refreshWidget();
                                    },
                                    errorFunction: (error) => {
                                        bulkLoader.next(false);
                                        this.widgetRef.notificationsService.showSnackBar(
                                            error &&
                                                error.error &&
                                                error.error.message
                                                ? error.error.message
                                                : 'Error deleting reports',
                                            true
                                        );
                                        this.widgetRef.refreshWidget();
                                    }
                                };
                                this.widgetRef.modalService.closeModal(
                                    null,
                                    modalId
                                );
                                new HitApi(
                                    apiArgs,
                                    this.widgetRef.httpService,
                                    this.widgetRef.ngZone
                                ).hitApi();
                            }
                        });
                    },
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-trash',
                        extraClass: 'color-warn'
                    },
                    buttonType: 'PRIMARY',
                    hoverText: 'Delete Selected Reports',
                    showLoader: false
                },
                {
                    buttonName: 'Enable',
                    function: (
                        buttonData: IButtonGeneratorInput,
                        selectedCards,
                        bulkLoader: BehaviorSubject<boolean>
                    ) => {
                        // Bulk Enable
                        bulkLoader.next(true);
                        const batchEnableInput = {
                            ids: selectedCards.map((selCard) => selCard.id)
                        };
                        const apiArgs: IHitApi = {
                            url: `${ApiUrls.REPORT_ENDPOINT}/batchEnable`,
                            intactUrl: `${ApiUrls.REPORT_ENDPOINT}/{batchEnable}`,
                            input: batchEnableInput,
                            uniqueIdentity: Symbol(),
                            requestType: RequestType.PATCH,
                            config: {
                                authorization: AuthorizationType.BEARER_TOKEN
                            },
                            function: (response) => {
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Reports enabed successfully'
                                );
                                bulkLoader.next(false);
                                this.widgetRef.refreshWidget();
                            },
                            errorFunction: (error) => {
                                bulkLoader.next(false);
                                this.widgetRef.notificationsService.showSnackBar(
                                    error && error.error && error.error.message
                                        ? error.error.message
                                        : 'Error enabling reports',
                                    true
                                );
                                this.widgetRef.refreshWidget();
                            }
                        };
                        new HitApi(
                            apiArgs,
                            this.widgetRef.httpService,
                            this.widgetRef.ngZone
                        ).hitApi();
                    },
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-power-off',
                        extraClass: 'color-success'
                    },
                    buttonType: 'PRIMARY',
                    hoverText: 'Enable Selected Reports',
                    showLoader: false
                },
                {
                    buttonName: 'Disable',
                    function: (
                        buttonData: IButtonGeneratorInput,
                        selectedCards,
                        bulkLoader: BehaviorSubject<boolean>
                    ) => {
                        // Bulk Disable
                        bulkLoader.next(true);
                        const batchDeleteInput = {
                            ids: selectedCards.map((selCard) => selCard.id)
                        };
                        const apiArgs: IHitApi = {
                            url: `${ApiUrls.REPORT_ENDPOINT}/batchDisable`,
                            intactUrl: `${ApiUrls.REPORT_ENDPOINT}/{batchDisable}`,
                            input: batchDeleteInput,
                            uniqueIdentity: Symbol(),
                            requestType: RequestType.PATCH,
                            config: {
                                authorization: AuthorizationType.BEARER_TOKEN
                            },
                            function: (response) => {
                                bulkLoader.next(false);
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Reports disabled successfully'
                                );
                                this.widgetRef.refreshWidget();
                            },
                            errorFunction: (error) => {
                                bulkLoader.next(false);
                                this.widgetRef.notificationsService.showSnackBar(
                                    error && error.error && error.error.message
                                        ? error.error.message
                                        : 'Error disabling reports',
                                    true
                                );
                                this.widgetRef.refreshWidget();
                            }
                        };
                        new HitApi(
                            apiArgs,
                            this.widgetRef.httpService,
                            this.widgetRef.ngZone
                        ).hitApi();
                    },
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-power-off',
                        extraClass: 'color-warn'
                    },
                    buttonType: 'PRIMARY',
                    hoverText: 'Enable Selected Reports',
                    showLoader: false
                }
            ],
            selection: 'multiple'
        };
    }

    create() {
        const modalInfo = {
            label: 'Create Report',
            purpose: ModalAction.CREATE
        };
        this.setModalDataAndOpen(modalInfo, null);
    }

    update(item) {
        const modalInfo = {
            label: 'Update Report',
            purpose: ModalAction.UPDATE
        };
        this.setModalDataAndOpen(modalInfo, item);
    }

    setModalDataAndOpen(modalInfo, stepData) {
        this.widgetRef.filterService.filtersDataTemp.delete(
            FilterStoreKey.MANAGED_REPORT_FILTERS
        );
        this.widgetRef.filterService.filtersData.delete(
            FilterStoreKey.MANAGED_REPORT_FILTERS
        );
        const modalData: IModalData = {
            modalName: modalInfo.label,
            modalHeightVh: 98,
            modalWidthVw: 85,
            modalIcon: {
                class: 'fas fa-file-invoice',
                type: IconType.FONTAWSOME
            } as IIcon,
            sourceId: this.widgetRef.widgetData.widgetUniqueIdentifier,
            modalType: ModalType.MIDDLE,
            modalPurpose: modalInfo.purpose,
            modalSteps: [
                {
                    stepData: {
                        componentToLoad: CreateMgReportStepOneComponent,
                        payload: {
                            data: {
                                widgetRef: this.widgetRef,
                                stepData,
                                edit: stepData ? true : false
                            }
                        }
                    },
                    stepName: 'Select Widgets'
                },
                {
                    stepData: {
                        componentToLoad: CreateMgReportStepTwoComponent,
                        payload: {
                            data: {
                                widgetRef: this.widgetRef,
                                stepData,
                                edit: stepData ? true : false
                            }
                        }
                    },
                    stepName: 'Filters'
                },
                {
                    stepData: {
                        componentToLoad: CreateMgReportStepThreeComponent,
                        payload: {
                            data: {
                                widgetRef: this.widgetRef,
                                stepData,
                                edit: stepData ? true : false
                            }
                        }
                    },
                    stepName: 'Scheduling'
                }
            ]
        };
        this.modalService.openModal(modalData);
    }

    delete(item) {
        this.modalService.openConfirmationModal({
            modalName: 'Delete Report',
            modalIcon: {
                type: IconType.FONTAWSOME,
                class: 'fas fa-trash'
            },
            contextIcon: {
                type: IconType.FONTAWSOME,
                class: 'fas fa-exclaimation-triangle'
            },
            confirmationMessage: 'Are you sure you want to delete report ?',
            buttonColorType: ButtonColorType.WARN,
            buttonText: 'Delete',
            loaderOnExec: true,
            function: (modalId: Symbol) => {
                this.deleteApi.call(this, item, modalId);
            }
        });
    }
    deleteApi(report, modalId: Symbol) {
        const deleteArgs = Helper.generateHitApiConfig(
            this.widgetRef.widgetData.widgetInfo.delete
        );
        deleteArgs.intactUrl = deleteArgs.url;
        deleteArgs.url = deleteArgs.url.replace('{report-id}', report.id);
        deleteArgs.input = {};
        deleteArgs.function = (response) => {
            this.widgetRef.notificationsService.showSnackBar(
                'Report deleted successfully'
            );
            this.widgetRef.modalService.closeModal(null, modalId);
            this.widgetRef.refreshWidget();
        };
        deleteArgs.errorFunction = (error) => {
            this.widgetRef.notificationsService.showSnackBar(
                error && error.error.message
                    ? error.error.message
                    : 'Error deleting report',
                true
            );
            this.widgetRef.modalService.closeModal(null, modalId);
        };
        new HitApi(
            deleteArgs,
            this.widgetRef.httpService,
            this.widgetRef.ngZone
        ).hitApi();
    }

    toggleScheduler(item): void {
        let status = '';
        const widgetRef: Widget = this.widgetRef;
        const apiArgs: IHitApi = {
            url: ``,
            requestType: RequestType.PATCH,
            input: {},
            function: (response) => {
                item.active = !item.active;
            },
            errorFunction: () => {
                this.widgetRef.refreshWidget();
            },
            uniqueIdentity: Symbol(),
            config: {
                authorization: AuthorizationType.BEARER_TOKEN,
                ignoreBaseUrl: false
            }
        };
        if (item.active) {
            status = 'disable';
        } else {
            status = 'enable';
        }
        apiArgs.intactUrl = `${ApiUrls.REPORT_ENDPOINT}/${status}/{report-id}`;
        apiArgs.url = `${ApiUrls.REPORT_ENDPOINT}/${status}/${item.id}`;
        new HitApi(apiArgs, widgetRef.httpService, widgetRef.ngZone).hitApi();
    }
}
